import { useState, useRef, useEffect, useContext } from 'react';
import { useSaveQuestions } from './useSaveQuestions';
import { LanguageContext } from '../context/LanguageProvider';
import { scrollToError, formatValidationErrors } from '../utils/utils';


const findAndUpdateQuestion = (questions, updatedQuestion) => {
  return questions.map((question) => {
    if (question.uniqueId === updatedQuestion.uniqueId) {
      return { ...question, ...updatedQuestion };
    } else if (question.subAreaGroupLevel2DtoList) {
      question.subAreaGroupLevel2DtoList = question.subAreaGroupLevel2DtoList.map(subGroup => ({
        ...subGroup,
        subAreaCompositionDtoList: subGroup.subAreaCompositionDtoList
          ? findAndUpdateQuestion(subGroup.subAreaCompositionDtoList, updatedQuestion)
          : []
      }));
    }
    return question;
  });
};

const integrateApiResponse = (currentStructure, apiResponse) => {
  return apiResponse.subAreaGroupLevel1DtoList.map((responseGroup) => {
    let existingGroup = currentStructure.find(group => group.productSubAreaId === responseGroup.productSubAreaId);

    if (!existingGroup) return responseGroup;

    existingGroup = { ...existingGroup, ...responseGroup };

    existingGroup.subAreaGroupLevel2DtoList = existingGroup.subAreaGroupLevel2DtoList.map((existingSubGroup) => {
      const responseSubGroup = responseGroup.subAreaGroupLevel2DtoList.find(
        sg => sg.productSubAreaId === existingSubGroup.productSubAreaId
      );

      if (!responseSubGroup || !responseSubGroup.subAreaCompositionDtoList) {
        return existingSubGroup;
      }

      responseSubGroup.subAreaCompositionDtoList.forEach(updatedQuestion => {
        existingSubGroup.subAreaCompositionDtoList = findAndUpdateQuestion(
          existingSubGroup.subAreaCompositionDtoList,
          updatedQuestion
        );
      });

      return existingSubGroup;
    });

    return existingGroup;
  });
};

export const useInputBlurSave = ({
  product,
  client,
  subArea,
  firstLevelArea,
  secondLevelArea,
  goToResults,
  inputRefs,
  setQuestions,
  setSubAreaName,
  setPctGroup1,
  setPctGroup2,
  validationErrors,
  setValidationErrors,
  setIsDataSaved,
  setNavigateToIdMatrix,
  openModal,
  fetchLanguageForClient,
}) => {
  const { saveQuestions } = useSaveQuestions();

  // Refs for the blur queue — using refs instead of module-level vars

  const blurUpdatingRef = useRef(null);
  const blurPendingRef = useRef(null);
  const saveQueueRef = useRef(Promise.resolve());
  const validationErrorsRef = useRef([]);

  const [inputBlurTriggered, setInputBlurTriggered] = useState(false);
  const [savingQuestionId, setSavingQuestionId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [updatedQuestions, setUpdatedQuestions] = useState([]);

  // Keep ref in sync with state so async callbacks always read the latest errors
  useEffect(() => {
    validationErrorsRef.current = validationErrors;
  }, [validationErrors]);

  const handleInputBlur = async (input) => {
    if (blurUpdatingRef.current == null) {
      blurUpdatingRef.current = input;
    } else {
      blurPendingRef.current = input;
      return;
    }

    setNavigateToIdMatrix(null);
    setIsSaving(true);

    saveQueueRef.current = saveQueueRef.current.then(async () => {
      return new Promise((resolveQueue) => {
        const questions = Array.isArray(input) ? input : [input];
        let newOtherProductId = null;

        const newQuestions = questions.map((question) => {
          newOtherProductId = question.otherProductId;
          let newValue = question.value;
          if (question.valueType === "REAL" || question.valueType === "INT") {
            newValue = newValue === null ? null : +newValue;
          }
          return { ...question, value: newValue };
        });

        // Modal trigger on matched additional info value
        newQuestions.forEach((question) => {
          const matchedInfo = question.additionalInfoPossibleValues?.find(
            (info) => String(info.value) === String(question.value)
          );
          if (matchedInfo) {
            openModal(matchedInfo.title, matchedInfo.content);
          }
        });

        setUpdatedQuestions((prev) =>
          prev
            .filter((q) => !newQuestions.some((newQ) => newQ.uniqueId === q.uniqueId))
            .concat(newQuestions)
        );

        try {
          const productToUse = newOtherProductId || product;
          const filteredQuestionsToUpdate = newQuestions.filter(
            (q) => !updatedQuestions.some((prevQ) => prevQ.uniqueId === q.uniqueId)
          );

          if (filteredQuestionsToUpdate.length > 0) {
            saveQuestions(
              filteredQuestionsToUpdate,
              productToUse,
              client,
              subArea,
              firstLevelArea,
              secondLevelArea,
              product,
              goToResults
            )
              .then((result) => {
                // Trigger language refresh if a LANGUAGE question was updated
                if (filteredQuestionsToUpdate.some((q) => q.uiRenderType === "LANGUAGE")) {
                  fetchLanguageForClient();
                }

                setInputBlurTriggered((prev) => !prev);

                const responseData = result.data || {};

                if (responseData.subAreaGroupLevel1DtoList) {
                  setQuestions((currentQuestions) =>
                    integrateApiResponse(currentQuestions, responseData)
                  );
                }

                if (responseData.name) setSubAreaName(responseData.name);
                setPctGroup1(responseData.pctGroup1);
                setPctGroup2(responseData.pctGroup2);
                setUpdatedQuestions([]);

                const apiValidationErrors = responseData.errorDto?.validationErrors || [];

                // Show modal for POSSIBLE_VALUES validation error
                const firstApiError = apiValidationErrors[0];
                if (firstApiError?.additionalInfoDto?.uiRenderType === "POSSIBLE_VALUES") {
                  openModal(
                    firstApiError.additionalInfoDto.title || "Error",
                    firstApiError.additionalInfoDto.content || firstApiError.errorText
                  );
                }

                // Helper: flatten questions from response for comparison
                const extractAllQuestions = (groupLevel1List = []) =>
                  groupLevel1List.flatMap((group1) =>
                    group1.subAreaGroupLevel2DtoList?.flatMap(
                      (group2) => group2.subAreaCompositionDtoList || []
                    ) || []
                  );

                // Merge, clean, and deduplicate validation errors
                setValidationErrors((prevErrors) => {
                  const allUpdatedQuestions = extractAllQuestions(
                    responseData.subAreaGroupLevel1DtoList || []
                  );
                  const skipStillExistsCheck = allUpdatedQuestions.length === 0;

                  const cleanedPrev = prevErrors.filter((prevError) => {
                    const wasJustPut = filteredQuestionsToUpdate.some(
                      (q) => q.id === prevError.id && q.compositionType === prevError.compositionType
                    );
                    const shouldReset = prevError.resetOnChange === true;
                    const stillExists = skipStillExistsCheck
                      ? true
                      : allUpdatedQuestions.some(
                          (q) => q.id === prevError.id && q.compositionType === prevError.compositionType
                        );

                    return stillExists && !wasJustPut && !(shouldReset && filteredQuestionsToUpdate.length > 0);
                  });

                  const merged = [
                    ...cleanedPrev.filter(
                      (prev) => !apiValidationErrors.some(
                        (newErr) => newErr.id === prev.id && newErr.compositionType === prev.compositionType
                      )
                    ),
                    ...apiValidationErrors,
                  ];

                  const deduped = merged.filter(
                    (error, index, self) =>
                      index === self.findIndex(
                        (e) => e.id === error.id && e.compositionType === error.compositionType
                      )
                  );

                  const formatted = formatValidationErrors(deduped);

                  if (formatted.length > 0) {
                    const firstError = formatted[0];
                    const errorQuestionId = `${firstError.compositionType}_${firstError.id}_${firstError.productId}`;
                    scrollToError(errorQuestionId, inputRefs);
                  }

                  return formatted;
                });

                setIsDataSaved(result.status === 200);

                setTimeout(() => {
                  blurUpdatingRef.current = null;
                  if (blurPendingRef.current != null) {
                    const toUpdate = blurPendingRef.current;
                    blurPendingRef.current = null;
                    handleInputBlur(toUpdate).then(() => resolveQueue());
                  } else {
                    resolveQueue();
                  }
                }, 50);
              })
              .catch((error) => {
                console.error("Error saving question:", error);
                blurPendingRef.current = null;
                resolveQueue();
              })
              .finally(() => {
                setSavingQuestionId(null);
                setIsSaving(false);
                setUpdatedQuestions([]);
              });
          } else {
            resolveQueue();
          }
        } catch (error) {
          console.error("Error saving question:", error);
          blurPendingRef.current = null;
          resolveQueue();
        }
      });
    });
  };

  return {
    handleInputBlur,
    inputBlurTriggered,
    savingQuestionId,
    setSavingQuestionId,
    isSaving,
    saveQueueRef,       // needed by validateQuestionsViaNext in Product
    validationErrorsRef, // needed by validateQuestionsViaNext in Product
  };
};
